import {
  prisma,
  UserRoleAssignmentEntityType,
  ParticipantGroupType,
  type Prisma,
} from "client";

export const TEAM_MEMBER_ERROR = {
  emailAlreadyInUse: "emailAlreadyInUse",
  invalidEmail: "invalidEmailAddress",
} as const;

export type OwnerInvite = {
  id?: string;
  userId?: string | null;
  email?: string | null;
};

export type TeamMemberPermissions = {
  adminSettings?: boolean;
  companySettings?: boolean;
  eventDayBadge?: boolean;
};

/** Default portal access for company owners (exhibitor + sponsor). */
export const OWNER_PORTAL_PERMISSIONS: TeamMemberPermissions = {
  adminSettings: true,
  companySettings: true,
  eventDayBadge: true,
};

const emailsMatch = (a?: string | null, b?: string | null) =>
  Boolean(a && b && a.toLowerCase() === b.toLowerCase());

export const pickLinkedOwnerInvitation = <T extends { userId?: string | null }>(
  invites: T[]
) => invites.find((invite) => Boolean(invite.userId)) ?? invites[0] ?? null;

export const isParticipantOwner = ({
  memberUserId,
  memberEmail,
  profileEmail,
  ownerInvite,
  companyEmail,
}: {
  memberUserId: string;
  memberEmail?: string | null;
  profileEmail?: string | null;
  ownerInvite?: OwnerInvite | null;
  companyEmail?: string | null;
}) => {
  if (ownerInvite?.userId) {
    return ownerInvite.userId === memberUserId;
  }
  const emailIsOwner = (email?: string | null) =>
    emailsMatch(ownerInvite?.email, email) || emailsMatch(companyEmail, email);
  return emailIsOwner(memberEmail) || emailIsOwner(profileEmail);
};

/** Remove event-scoped team profiles when deleting a company. Users are kept (owners have FormTemplates etc.). */
export async function cleanupCompanyTeamUsers(
  tx: Prisma.TransactionClient,
  {
    teamUserIds,
    eventId,
    participantGroupId,
    companyEntityId,
  }: {
    teamUserIds: string[];
    eventId: string;
    participantGroupId: string | null;
    companyEntityId: string;
    entityType: UserRoleAssignmentEntityType;
  }
) {
  if (!teamUserIds.length) return;

  const [onOtherExhibitors, onOtherSponsors] = await Promise.all([
    tx.user.findMany({
      where: {
        id: { in: teamUserIds },
        exhibitors: {
          some: { eventId, id: { not: companyEntityId } },
        },
      },
      select: { id: true },
    }),
    tx.user.findMany({
      where: {
        id: { in: teamUserIds },
        sponsors: {
          some: { eventId, id: { not: companyEntityId } },
        },
      },
      select: { id: true },
    }),
  ]);
  const sharedAtEvent = new Set(
    [...onOtherExhibitors, ...onOtherSponsors].map((user) => user.id)
  );
  const userIdsToCleanup = teamUserIds.filter((id) => !sharedAtEvent.has(id));
  if (!userIdsToCleanup.length) return;

  const profiles = await tx.profile.findMany({
    where: { userId: { in: userIdsToCleanup }, eventId },
    select: { id: true },
  });
  const profileIds = profiles.map((profile) => profile.id);
  if (!profileIds.length) return;

  if (participantGroupId) {
    await tx.participantGroup.update({
      where: { id: participantGroupId },
      data: {
        participants: {
          disconnect: profileIds.map((id) => ({ id })),
        },
      },
    });
  }

  await Promise.all([
    tx.formEntity.deleteMany({ where: { entityID: { in: profileIds } } }),
    tx.profile.deleteMany({ where: { id: { in: profileIds } } }),
  ]);
}

export const toPortalTeamMember = <
  TProfile extends {
    id: string;
    eventId: string;
    name?: string | null;
    email?: string | null;
    jobTitle?: string | null;
    exhibitorPermissions?: TeamMemberPermissions | null;
  }
>(
  member: {
    id: string;
    name?: string | null;
    email?: string | null;
    profiles?: TProfile[];
  },
  eventId: string,
  ownerInvite?: OwnerInvite | null,
  companyEmail?: string | null
) => {
  const profile =
    member.profiles?.find((p) => p.eventId === eventId) ?? member.profiles?.[0];
  return {
    id: member.id,
    name: profile?.name || member.name,
    email: profile?.email || member.email,
    jobTitle: profile?.jobTitle ?? null,
    isOwner: isParticipantOwner({
      memberUserId: member.id,
      memberEmail: member.email,
      profileEmail: profile?.email,
      ownerInvite,
      companyEmail,
    }),
    exhibitorPermissions: profile?.exhibitorPermissions ?? null,
    profiles: profile
      ? [
          {
            id: profile.id,
            eventId: profile.eventId,
            exhibitorPermissions: profile.exhibitorPermissions,
          },
        ]
      : [],
  };
};

const hasPortalPermissions = (perms?: TeamMemberPermissions | null) =>
  Boolean(
    perms?.adminSettings || perms?.companySettings || perms?.eventDayBadge
  );

const getSuperAdminEntityIds = async (
  userId: string,
  entityType: UserRoleAssignmentEntityType,
  roleName: string
) => {
  const assignments = await prisma.assignmentModel.findMany({
    where: {
      userId,
      entityType,
      role: { name: roleName },
      entityId: { not: null },
    },
    select: { entityId: true },
  });
  return assignments
    .map((a) => a.entityId)
    .filter((id): id is string => Boolean(id));
};

/**
 * ponytail: lazy backfill for company owners (exhibitor + sponsor) missing team/permissions.
 * Runs on session capability build and portal entry — links owner invitation userId,
 * connects team when missing, seeds profile.exhibitorPermissions when absent.
 */
export async function ensureCompanyOwnerPortalAccess(
  userId: string,
  eventId: string,
  companyType: ParticipantGroupType
): Promise<TeamMemberPermissions | null> {
  const isExhibitor = companyType === ParticipantGroupType.EXHIBITORS;
  const entityType = isExhibitor
    ? UserRoleAssignmentEntityType.EXHIBITOR
    : UserRoleAssignmentEntityType.SPONSOR;
  const superAdminRole = isExhibitor
    ? "EXHIBITOR_SUPER_ADMIN"
    : "SPONSOR_SUPER_ADMIN";
  const ownerInviteSelect = {
    where: { isOwner: true as const },
    select: { id: true, userId: true, email: true },
    orderBy: { updatedAt: "desc" as const },
  };
  const companySelect = {
    id: true,
    email: true,
    participantGroupId: true,
    ...(isExhibitor
      ? { exhibitorInvitations: ownerInviteSelect }
      : { sponsorInvitations: ownerInviteSelect }),
  };

  let company: {
    id: string;
    email: string | null;
    participantGroupId?: string | null;
    exhibitorInvitations?: OwnerInvite[];
    sponsorInvitations?: OwnerInvite[];
  } | null = isExhibitor
    ? await prisma.exhibitor.findFirst({
        where: { eventId, teamIds: { has: userId } },
        select: companySelect,
      })
    : await prisma.sponsor.findFirst({
        where: { eventId, teamIds: { has: userId } },
        select: companySelect,
      });

  if (!company) {
    const entityIds = await getSuperAdminEntityIds(
      userId,
      entityType,
      superAdminRole
    );
    if (!entityIds.length) return null;
    company = isExhibitor
      ? await prisma.exhibitor.findFirst({
          where: { eventId, id: { in: entityIds } },
          select: companySelect,
        })
      : await prisma.sponsor.findFirst({
          where: { eventId, id: { in: entityIds } },
          select: companySelect,
        });
  }
  if (!company) return null;

  const ownerInvite = pickLinkedOwnerInvitation(
    company.exhibitorInvitations ?? company.sponsorInvitations ?? []
  );
  const [user, superAdminAssignment, profile] = await Promise.all([
    prisma.user.findUnique({
      where: { id: userId },
      select: { email: true },
    }),
    prisma.assignmentModel.findFirst({
      where: {
        userId,
        entityId: company.id,
        entityType,
        role: { name: superAdminRole },
      },
      select: { id: true },
    }),
    prisma.profile.findFirst({
      where: { userId, eventId },
      select: { id: true, email: true, exhibitorPermissions: true },
    }),
  ]);

  const isOwner =
    Boolean(superAdminAssignment) ||
    isParticipantOwner({
      memberUserId: userId,
      memberEmail: user?.email,
      profileEmail: profile?.email,
      ownerInvite,
      companyEmail: company.email,
    });
  if (!isOwner) return null;

  const onTeam = isExhibitor
    ? await prisma.exhibitor.findFirst({
        where: { id: company.id, teamIds: { has: userId } },
        select: { id: true },
      })
    : await prisma.sponsor.findFirst({
        where: { id: company.id, teamIds: { has: userId } },
        select: { id: true },
      });
  if (!onTeam) {
    if (isExhibitor) {
      await prisma.exhibitor.update({
        where: { id: company.id },
        data: { team: { connect: { id: userId } } },
      });
    } else {
      await prisma.sponsor.update({
        where: { id: company.id },
        data: { team: { connect: { id: userId } } },
      });
    }
  }

  if (ownerInvite?.id && ownerInvite.userId !== userId) {
    await prisma.participantInvitation.update({
      where: { id: ownerInvite.id },
      data: { userId },
    });
  }

  const current = profile?.exhibitorPermissions as TeamMemberPermissions | null;
  const targetPermissions = OWNER_PORTAL_PERMISSIONS;

  if (!profile) return targetPermissions;

  if (
    current?.adminSettings &&
    current?.companySettings &&
    current?.eventDayBadge === targetPermissions.eventDayBadge
  ) {
    return current;
  }

  await prisma.profile.update({
    where: { id: profile.id },
    data: { exhibitorPermissions: targetPermissions },
  });
  return targetPermissions;
}

/** @deprecated prefer ensureCompanyOwnerPortalAccess */
export async function ensureSponsorOwnerPortalAccess(
  userId: string,
  eventId: string
): Promise<TeamMemberPermissions | null> {
  return ensureCompanyOwnerPortalAccess(
    userId,
    eventId,
    ParticipantGroupType.SPONSORS
  );
}

/** Team membership or super-admin assignment for this event. */
export async function resolveCompanyTypeForUser(
  userId: string,
  eventId: string
): Promise<ParticipantGroupType | null> {
  const [onExhibitorTeam, onSponsorTeam, fromAssignment] = await Promise.all([
    prisma.exhibitor.findFirst({
      where: { eventId, teamIds: { has: userId } },
      select: { id: true },
    }),
    prisma.sponsor.findFirst({
      where: { eventId, teamIds: { has: userId } },
      select: { id: true },
    }),
    getCompanyTypeFromSuperAdminAssignment(userId, eventId),
  ]);
  if (onExhibitorTeam) return ParticipantGroupType.EXHIBITORS;
  if (onSponsorTeam) return ParticipantGroupType.SPONSORS;
  return fromAssignment;
}

/** Super-admin assignment scoped to this event (teamIds may be missing on legacy sponsors). */
export async function getCompanyTypeFromSuperAdminAssignment(
  userId: string,
  eventId: string
): Promise<ParticipantGroupType | null> {
  const assignments = await prisma.assignmentModel.findMany({
    where: {
      userId,
      entityId: { not: null },
      entityType: {
        in: [
          UserRoleAssignmentEntityType.EXHIBITOR,
          UserRoleAssignmentEntityType.SPONSOR,
        ],
      },
      role: {
        name: { in: ["EXHIBITOR_SUPER_ADMIN", "SPONSOR_SUPER_ADMIN"] },
      },
    },
    select: { entityType: true, entityId: true },
  });

  for (const assignment of assignments) {
    if (
      assignment.entityType === UserRoleAssignmentEntityType.EXHIBITOR &&
      assignment.entityId
    ) {
      const exhibitor = await prisma.exhibitor.findFirst({
        where: { id: assignment.entityId, eventId },
        select: { id: true },
      });
      if (exhibitor) return ParticipantGroupType.EXHIBITORS;
    }
    if (
      assignment.entityType === UserRoleAssignmentEntityType.SPONSOR &&
      assignment.entityId
    ) {
      const sponsor = await prisma.sponsor.findFirst({
        where: { id: assignment.entityId, eventId },
        select: { id: true },
      });
      if (sponsor) return ParticipantGroupType.SPONSORS;
    }
  }
  return null;
}
